import * as dto from '@epicuro-next/data/baskets-dto';
import {
  BasketRowsGroup,
  BasketRowsGroupType,
} from 'graphql-api/baskets-api-interfaces';

import { getNestedObjects, GetRowsFn, GroupingFn, Mappings } from '../mappings';
import { isDtoBasketChecked, isDtoBasketPlaced } from '../shared';

/**
 * Second level grouping by delivery date.
 */
export function groupByDelivery(
  items: dto.Item[],
  maps: Mappings,
  groupFn?: GroupingFn,
  getRowsFn?: GetRowsFn,
): BasketRowsGroup[] {
  const deliveries = maps.basket.basketAccounts.flatMap(
    (basketAccount) => basketAccount.deliveries,
  );
  const deliveryItemsMap = groupItemsByDelivery(items);

  return Object.keys(deliveryItemsMap).map((deliveryId) => {
    const deliveryItems = deliveryItemsMap[deliveryId];

    const nested = getNestedObjects(deliveryItems, maps, groupFn, getRowsFn);
    const isBasketCheckedOrNotNew =
      isDtoBasketChecked(maps.basket) || isDtoBasketPlaced(maps.basket);

    return {
      id: deliveryItems?.[0].deliveryId,
      type: BasketRowsGroupType.Delivery,
      details: getDeliveryDetails(deliveryItems, maps),
      title: getDeliveryDate(
        deliveryItems?.[0],
        deliveries,
        isBasketCheckedOrNotNew,
      ),
      ...nested,
    };
  });
}

function groupItemsByDelivery(items: dto.Item[]) {
  return items?.reduce(
    (result, item) => ({
      ...result,
      [item.deliveryId]: [...(result[item.deliveryId] || []), item],
    }),
    {} as { [deliveryId: string]: dto.Item[] },
  );
}

function getDeliveryDate(
  item: dto.Item,
  deliveries: dto.Delivery[],
  isBasketCheckedOrNotNew = false,
) {
  return isBasketCheckedOrNotNew
    ? item.expectedDeliveryDate ?? getRequestedDeliveryDate(item, deliveries)
    : getRequestedDeliveryDate(item, deliveries);
}

function getRequestedDeliveryDate(item: dto.Item, deliveries: dto.Delivery[]) {
  return (
    deliveries.find((delivery) => item.deliveryId === delivery.deliveryId)
      ?.requestedDeliveryDate ?? item.requestedDeliveryDate
  );
}

function getDeliveryDetails(items: dto.Item[], maps: Mappings) {
  const basketAccountId = items?.[0]?.basketAccountId || '';
  const basketAccount = maps.basketAccounts[basketAccountId];
  const delivery = basketAccount.deliveries.find(
    (d) => d.deliveryId === items?.[0].deliveryId,
  );

  const consignmentDetails = delivery?.isConsignmentOrder
    ? {
        consignmentStartDate: delivery?.requestedDeliveryDate,
        consignmentEndDate: delivery?.consignmentEndDate,
      }
    : {};

  return {
    deliveryId: items?.[0].deliveryId,
    clientReferenceNumber: delivery?.clientReferenceNumber,
    generatedClientReferenceNumber: delivery?.generatedClientReferenceNumber,
    isConsignmentOrder: delivery?.isConsignmentOrder,
    ...consignmentDetails,
  };
}
